function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import PropTypes from "prop-types";
import React, { Component, cloneElement } from "react";
import ReactDOM from "react-dom";
import { createPopper } from "@popperjs/core";
import omit from "lodash.omit";
import classnames from "classnames";
import { findComponent } from "@brandwatch/axiom-utils";
import Portal from "../Portal/Portal";
import { PositionSourceRef } from "./PositionSource";
import { PositionTargetRef } from "./PositionTarget";
import { placementToPosition, positionToPlacement, getPlacementFlipOrder } from "./_utils";
import "./Position.css";

/* eslint-disable react/no-find-dom-node */

/**
 * Position is a wrapper around PopperJs. It is used within Axiom by the Dropdown and Tooltip component.
 * You can use it if you want more control or to create custom components.
 */
export default class Position extends Component {
  constructor(props) {
    super(props);
    this.handleOnCreate = this.handleOnCreate.bind(this);
    this.handleOnUpdate = this.handleOnUpdate.bind(this);
    this.state = {
      placement: positionToPlacement(props.position, props.offset)
    };
  }
  componentDidMount() {
    if (this._content) {
      this._popper = this.createPopper();
    }
  }
  componentDidUpdate() {
    const {
      enabled,
      isVisible,
      position,
      offset
    } = this.props;
    if (enabled && isVisible) {
      if (!this._popper) {
        this._popper = this.createPopper();
      } else {
        const placement = positionToPlacement(position, offset);
        this._popper.setOptions({
          placement
        });
        this._popper.forceUpdate();
      }
    } else if (this._popper) {
      this.destroyPopper();
    }
  }
  componentWillUnmount() {
    this.destroyPopper();
  }
  createPopper() {
    const {
      boundariesElement,
      flip,
      showArrow,
      reference,
      popperModifiers
    } = this.props;
    const {
      placement
    } = this.state;
    const modifiers = [{
      name: "afterWrite",
      enabled: true,
      phase: "afterWrite",
      fn: this.handleOnUpdate
    }, {
      name: "arrow",
      options: {
        enabled: showArrow,
        element: this._arrow
      }
    }, {
      name: "flip",
      options: {
        fallbackPlacements: getPlacementFlipOrder(placement, flip)
      }
    }, {
      name: "offset",
      options: {
        offset: () => {
          if (showArrow) {
            return [0, 15];
          } else {
            return [2, 2];
          }
        }
      }
    }, {
      name: "preventOverflow",
      options: {
        boundariesElement
      }
    }, ...popperModifiers];
    return createPopper(reference || this._target, this._content, {
      onFirstUpdate: this.handleOnCreate,
      placement,
      modifiers
    });
  }
  destroyPopper() {
    if (this._popper) {
      this._popper.destroy();
    }
    delete this._popper;
    delete this._arrow;
    delete this._content;
    const placement = positionToPlacement(this.props.position, this.props.offset);
    if (placement !== this.state.placement) {
      this.setState({
        placement
      });
    }
  }
  handleOnCreate(popper) {
    const {
      onPositionChange
    } = this.props;
    if (onPositionChange) {
      onPositionChange(popper.placement);
    }
  }
  handleOnUpdate(_ref) {
    let {
      state: {
        placement
      }
    } = _ref;
    const {
      onPositionChange
    } = this.props;
    const {
      placement: statePlacement
    } = this.state;
    if (statePlacement !== placement) {
      this.setState({
        placement
      });
      if (onPositionChange) {
        onPositionChange(placement);
      }
    }
  }
  render() {
    const {
      children,
      className,
      enabled,
      isVisible,
      onMaskClick,
      showArrow,
      reference,
      ...rest
    } = this.props;
    const {
      placement
    } = this.state;
    const [position] = placementToPosition(placement);
    const classes = classnames("ax-position", {
      "ax-position--arrow": showArrow
    }, className);
    const props = omit(rest, ["flip", "offset", "position", "onPositionChange"]);
    return [!reference && /*#__PURE__*/cloneElement(findComponent(children, PositionTargetRef), {
      ref: target => this._target = ReactDOM.findDOMNode(target)
    }), enabled && isVisible ? /*#__PURE__*/React.createElement(Portal, _extends({}, props, {
      key: "portal"
    }), /*#__PURE__*/React.createElement("div", null, onMaskClick && /*#__PURE__*/React.createElement("div", {
      className: "ax-position__mask",
      onClick: onMaskClick
    }), /*#__PURE__*/React.createElement("div", {
      className: classes,
      ref: el => this._content = el
    }, /*#__PURE__*/cloneElement(findComponent(children, PositionSourceRef), {
      arrowRef: showArrow ? arrow => this._arrow = ReactDOM.findDOMNode(arrow) : undefined,
      position
    })))) : null];
  }
}
_defineProperty(Position, "defaultProps", {
  boundariesElement: "viewport",
  enabled: true,
  flip: "clockwise",
  offset: "middle",
  position: "top",
  showArrow: false,
  popperModifiers: []
});